<script setup>
import {
  ref,
  computed,
  reactive,
  defineEmits
} from 'vue';

import { DEFAULT_SORT } from '../composable/useVideos';

import VideoSearch from "../modules/VideoSearch";
import ListCategories from '../modules/ListCategories.vue';

const props = defineProps({
  videos: {
    type: Array,
    default: () => ([]),
  },
  categories: {
    type: Array,
    default: () => ([]),
  },
  selectedCategories: {
    type: Array,
    default: () => ([]),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  showMore: {
    type: Boolean,
    default: false,
  },
  typeFilterName: {
    type: String,
    default: '',
  }
});

const emit = defineEmits([
    'handle-load-more',
    'handle-selected-category',
    'handle-change-sort'
])

const sort = reactive({
  ...DEFAULT_SORT
})

const isModalOpened = ref(false);
const selectedOrder = ref('newest');

const mapVideos = computed(() => {
  return (props.videos || []).map((video) => {
    const videoCategories = video.taxonomies['video-category'];
    const isSponsored = (videoCategories || []).findIndex((videoCategory) => videoCategory.slug === 'sponsored-content') > -1;

    return {
      ...video,
      isSponsored,
    }
  })
})

function selectedCategory (category) {
  if (!category) {
    emit('handle-selected-category', [])
    return;
  }

  let categories = props.selectedCategories

  const isExist = (props.selectedCategories || []).findIndex((item) => item.id === category.id);

  if (isExist > -1) {
    categories = categories.filter((item) => item.id !== category.id);
  } else {
    categories = [
        ...categories,
        category
    ];
  }

  emit('handle-selected-category', categories);
}

function unSelectedCategory (category) {
  const items = props.selectedCategories.filter((item) => item.id !== category.id);

  emit('handle-selected-category', items);
}

function handleChangeSort ({ target }) {
  if (target.value === 'newest') {
    sort.order = 'desc'
    sort.orderBy = 'date';
  } else {
    sort.order = target.value;
    sort.orderBy = 'title';
  }

  selectedOrder.value = target.value;

  emit('handle-change-sort', sort);
}

function handleLoadMore () {
  emit('handle-load-more')
}

const openMobileModal = () => {
  isModalOpened.value = true;
}

const onClickOutside = () => {
  isModalOpened.value = false;
}
</script>

<template>
  <div>
    <slot name="heading" />

    <div class="page-video-category">
      <div class="row">
        <div class="col-md-3 page-video__asside">
          <slot name="category-name" />

          <div class="page-video__asside-content">
            <div class="mb-32">
              <a href="/video-portal" class="back">
                <span>
                  <svg xmlns:xlink="http://www.w3.org/1999/xlink" xmlns="http://www.w3.org/2000/svg" version="1.1" width="12px" height="12px">
                    <g transform="matrix(1 0 0 1 -381 -168 )">
                      <path d="M 12 6.75  L 12 5.25  L 2.8725 5.25  L 7.061249999999999 1.06125  L 6 0  L 0 6  L 6 12  L 7.061249999999999 10.93875  L 2.8725 6.75  L 12 6.75  Z " fill-rule="nonzero" fill="#a3277b" stroke="none" transform="matrix(1 0 0 1 381 168 )"/>
                    </g>
                  </svg>
                </span>
                Back to all videos
              </a>

              <video-search />

              <slot name="categories" />
            </div>

            <div>
              <div class="tags-header">
                <div class="tags-header__title">
                  <h4>{{ typeFilterName }}</h4>

                  <button
                      type="button"
                      v-show="selectedCategories.length > 0"
                      @click="selectedCategory(null)"
                  >
                    Unselect all
                  </button>
                </div>
              </div>

              <ListCategories
                v-if="categories"
                :items="categories"
                @selected="selectedCategory"
              />
            </div>
          </div>
        </div>

        <div class="asside-modal__bg" v-if="isModalOpened">
          <div class="asside-modal__body" v-click-outside="onClickOutside">
            <i
                @click="onClickOutside"
                class="asside-modal__close"
            >
                <svg
                    xmlns:xlink="http://www.w3.org/1999/xlink"
                    xmlns="http://www.w3.org/2000/svg"
                    version="1.1"
                    width="9px"
                    height="9px"
                >
                    <g transform="matrix(1 0 0 1 -477 -405 )">
                    <path
                        d="M 5.409642857142856 4.5  L 9 0.9096428571428572  L 8.090357142857144 0  L 4.5 3.590357142857143  L 0.9096428571428572 0  L 0 0.9096428571428572  L 3.590357142857143 4.5  L 0 8.090357142857144  L 0.9096428571428572 9  L 4.5 5.409642857142856  L 8.090357142857144 9  L 9 8.090357142857144  L 5.409642857142856 4.5  Z "
                        fill-rule="nonzero"
                        fill="#a3277b"
                        stroke="none"
                        transform="matrix(1 0 0 1 477 405 )"
                    />
                    </g>
                </svg>
            </i>

            <div class="asside-modal__content">
              <div class="page-video__asside--mobile">
                <slot name="category-name" />

                <div class="page-video__asside-content">
                  <div class="mb-32">
                    <video-search />

                    <slot name="categories" />
                  </div>

                  <div>
                    <div class="tags-header">
                      <div class="tags-header__title">
                        <h4>{{ typeFilterName }}</h4>

                        <button
                            type="button"
                            v-show="selectedCategories.length > 0"
                            @click="selectedCategory(null)"
                        >
                          Unselect all
                        </button>
                      </div>

                      <ul
                        class="active-tags-list active-tags-list--mobile"
                        v-show="selectedCategories.length > 0"
                      >
                        <li
                          v-for="category in selectedCategories"
                          :key="`active-${category.slug}`"
                        >
                          <button
                            type="button"
                          >
                            {{ category.name }}

                            <span @click="unSelectedCategory(category)">
                              <svg
                                xmlns:xlink="http://www.w3.org/1999/xlink"
                                xmlns="http://www.w3.org/2000/svg"
                                version="1.1"
                                width="9px"
                                height="9px"
                              >
                                <g transform="matrix(1 0 0 1 -477 -405 )">
                                  <path
                                    d="M 5.409642857142856 4.5  L 9 0.9096428571428572  L 8.090357142857144 0  L 4.5 3.590357142857143  L 0.9096428571428572 0  L 0 0.9096428571428572  L 3.590357142857143 4.5  L 0 8.090357142857144  L 0.9096428571428572 9  L 4.5 5.409642857142856  L 8.090357142857144 9  L 9 8.090357142857144  L 5.409642857142856 4.5  Z "
                                    fill-rule="nonzero"
                                    fill="#a3277b"
                                    stroke="none"
                                    transform="matrix(1 0 0 1 477 405 )"
                                  />
                                </g>
                              </svg>
                            </span>
                          </button>
                        </li>
                      </ul>
                    </div>

                    <ListCategories
                        v-if="categories"
                        :items="categories"
                        @selected="selectedCategory"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-9">
          <div class="page-video__main">
            <div class="page-video__main__sort">
              <div>
                <ul
                  class="active-tags-list active-tags-list--desktop"
                  v-show="selectedCategories.length > 0"
                >
                  <li
                    v-for="category in selectedCategories"
                    :key="`active-${category.slug}`"
                  >
                    <button>
                      {{ category.name }}

                      <span @click="unSelectedCategory(category)">
                        <svg
                          xmlns:xlink="http://www.w3.org/1999/xlink"
                          xmlns="http://www.w3.org/2000/svg"
                          version="1.1"
                          width="9px"
                          height="9px"
                        >
                          <g transform="matrix(1 0 0 1 -477 -405 )">
                            <path
                              d="M 5.409642857142856 4.5  L 9 0.9096428571428572  L 8.090357142857144 0  L 4.5 3.590357142857143  L 0.9096428571428572 0  L 0 0.9096428571428572  L 3.590357142857143 4.5  L 0 8.090357142857144  L 0.9096428571428572 9  L 4.5 5.409642857142856  L 8.090357142857144 9  L 9 8.090357142857144  L 5.409642857142856 4.5  Z "
                              fill-rule="nonzero"
                              fill="#a3277b"
                              stroke="none"
                              transform="matrix(1 0 0 1 477 405 )"
                            />
                          </g>
                        </svg>
                      </span>
                    </button>
                  </li>
                </ul>

                <div class="page-video__sort">
                  <button class="filter-button" @click="openMobileModal">
                    <span>Filter</span>
                  </button>

                  <div class="video-sort">
                    <label for="sort">Sort by:</label>
                    <select
                      :value="selectedOrder"
                      name="sort"
                      @change="handleChangeSort"
                    >
                      <option value="newest" selected>Newest</option>
                      <option value="asc">Title: ASC</option>
                      <option value="desc">Title: DESC</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div v-show="loading">
              Searching...
            </div>

            <div
              v-show="!loading && videos.length === 0"
              class="video-error"
            >
              <h2>Sorry, no videos found</h2>
            </div>

            <div v-if="videos.length > 0" class="videos-wrapper">
              <div
                v-for="video in mapVideos"
                :key="video.id"
                class="video"
              >
                <a
                  :href="video.link"
                  :class="{ 'sponsored-border': video.isSponsored }"
                >
                  <div
                    class="video__iframe disable-click-iframe"
                    v-html="video.meta.video"
                  />
                </a>

                <div class="video__body">
                  <div class="video__body__content">
                    <h3>
                      <a :href="video.link" v-html="video.title.rendered" />

                      <span
                          v-if="video.isSponsored"
                          class="post-sponsored"
                      >
                      <img src="/dist/img/ico_sponsored.svg" alt="ico-sponsored">
                      Sponsored content
                    </span>
                    </h3>

                    <div v-show="video.contentSliced.length > 3">
                      <p v-html="video.contentSliced" />
                      <a
                        v-show="video.contentSliced.length > 0"
                        :href="video.link"
                        class="read-more"
                      >
                        <span>Read more</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="page-video__load-more">
            <button
              v-show="showMore"
              @click.prevent="handleLoadMore"
              class="btn load-more"
            >
              <span>Load more videos</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
  ::v-deep .video-search {
    width: 100%;
  }
</style>
